import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

// ===== TOAST COMPONENT =====
const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => { onClose(); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast, onClose]);

  if (!toast) return null;

  return (
    <div style={{
      position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
      display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
      borderRadius: 14, fontSize: 14, fontWeight: 600, zIndex: 9999,
      minWidth: 280, maxWidth: '90vw',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      background: toast.type === 'success' ? 'rgba(46,125,50,0.95)' : 
                  toast.type === 'error' ? 'rgba(198,40,40,0.95)' : 
                  'rgba(26,35,126,0.95)',
      color: '#fff',
      animation: 'toastIn 0.3s ease'
    }}>
      <span>{toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}</span>
      <span style={{ flex: 1, fontSize: 13 }}>{toast.message}</span>
      <button onClick={onClose} style={{
        background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
        width: 24, height: 24, borderRadius: '50%', fontSize: 12, cursor: 'pointer'
      }}>✕</button>
    </div>
  );
};

// ===== TOAST CONTEXT =====
const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const success = useCallback((msg) => showToast(msg, 'success'), [showToast]);
  const error = useCallback((msg) => showToast(msg, 'error'), [showToast]);
  const info = useCallback((msg) => showToast(msg, 'info'), [showToast]);

  return (
    <ToastContext.Provider value={{ success, error, info, showToast }}>
      {children}
      <Toast toast={toast} onClose={hideToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};